import { KeycloakConfig } from '@react-keycloak/keycloak-ts';

export const LOCALES = ['en', 'zh'] as const;

export type Locale = typeof LOCALES[number];
export type JsonUrl = Record<Locale, string>;

export const CMS_LOCALE_MAP: Record<Locale, string> = {
  en: 'en-US',
  zh: 'zh',
};

declare interface AppConfig {
  baseCmsUrl?: string;
  baseTeladocUrl: string;
  baseUrl: string;
  baseApiUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  captchaSiteKey: string;
  paymentReturnUrl?: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: {
        bankTransferInfo: JsonUrl;
        chequeInfo: JsonUrl;
      };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  tagManagerArgs?: {
    gtmId: string;
  };
  userGuideUrl: string;
}

export const getJsonUrl = (baseUrl: string): JsonUrl => {
  const result: JsonUrl = { en: '', zh: '' };

  for (const key of LOCALES) {
    result[key] = `${baseUrl}/${CMS_LOCALE_MAP[key]}.json`;
  }

  return result;
};

const getCmsUrl = (baseCmsUrl: string): AppConfig['cmsUrl'] => ({
  json: {
    i18n: getJsonUrl(`${baseCmsUrl}/json/i18n`),
    shortfall: {
      bankTransferInfo: getJsonUrl(`${baseCmsUrl}/json/shortfall/bank-transfer`),
      chequeInfo: getJsonUrl(`${baseCmsUrl}/json/shortfall/cheque`),
    },
    faq: getJsonUrl(`${baseCmsUrl}/json/faq`),
    lifeFaq: getJsonUrl(`${baseCmsUrl}/json/lifefaq`),
  },
});

const PAYMENT_RETURN_PATH = `${location.origin}/payment/return`;
const SIT_URL = 'https://all-in-one-bravo-sit.generali.com.hk';
const SIT2_URL = 'https://genbravo-sit2.generali.com.hk';
const UAT_URL = 'https://all-in-one-bravo-uat.generali.com.hk';
const UAT2_URL = 'https://genbravo-uat2.generali.com.hk';
const PROD_URL = 'https://genbravo.generali.com.hk';
const SIT_API_URL = 'https://sit-api.generali.com.hk';
const UAT_API_URL = 'https://uat-api.generali.com.hk';
const PROD_API_URL = 'https://api.generali.com.hk';
const CAPTCHA_SITE_KEY = '6LdReoggAAAAAPOH__XoXtNd6pOg2dkco60QnTQI';
const BASE_TELADOC_URL = 'https://single-generalitest.meetv.com.cn';
const GTM_SIT = 'GTM-W5W5WNV';
const GTM_UAT = 'GTM-W5W5WNV';
const GTM_PROD = 'GTM-TV9RS9GZ';

// TODO: define production configuration when have more information
class DevelopmentConfig implements AppConfig {
  baseUrl = 'http://sit.app-generali.com';
  baseApiUrl = SIT_API_URL;
  baseCmsUrl: string;
  baseTeladocUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  paymentReturnUrl!: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: { bankTransferInfo: JsonUrl; chequeInfo: JsonUrl };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  tagManagerArgs = {
    gtmId: GTM_SIT,
  };
  captchaSiteKey: string;
  userGuideUrl: string;

  constructor() {
    this.baseCmsUrl = SIT_URL + '/site-content';
    this.baseTeladocUrl = BASE_TELADOC_URL;
    this.keycloak = {
      url: 'https://sit-api.generali.com.hk/auth-keycloak',
      realm: 'all-in-one-bravo',
      clientId: 'react-auth',
    };
    this.cmsUrl = getCmsUrl(this.baseCmsUrl);
    this.googleMapApiKey = 'AIzaSyCbwYjPPmBe-6bk0wbnuDG4L_gIXtaRsak';
    this.paymentReturnUrl = PAYMENT_RETURN_PATH;
    this.captchaSiteKey = CAPTCHA_SITE_KEY;
    this.userGuideUrl = 'https://aiob-sitecontent-sit.s3.ap-east-1.amazonaws.com/user-guide/GenBravo_UserGuide_web_';
  }
}

class DevelopmentConfig2 implements AppConfig {
  baseUrl = 'http://sit.app-generali.com';
  baseApiUrl = SIT_API_URL;
  baseCmsUrl: string;
  baseTeladocUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  paymentReturnUrl!: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: { bankTransferInfo: JsonUrl; chequeInfo: JsonUrl };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  tagManagerArgs = {
    gtmId: GTM_SIT,
  };
  captchaSiteKey: string;
  userGuideUrl: string;

  constructor() {
    this.baseCmsUrl = SIT2_URL + '/site-content';
    this.baseTeladocUrl = BASE_TELADOC_URL;
    this.keycloak = {
      url: 'https://sit-api.generali.com.hk/auth-keycloak',
      realm: 'all-in-one-bravo',
      clientId: 'react-auth',
    };
    this.cmsUrl = getCmsUrl(this.baseCmsUrl);
    this.googleMapApiKey = 'AIzaSyCbwYjPPmBe-6bk0wbnuDG4L_gIXtaRsak';
    this.paymentReturnUrl = PAYMENT_RETURN_PATH;
    this.captchaSiteKey = CAPTCHA_SITE_KEY;
    this.userGuideUrl = 'https://aiob-sitecontent-sit.s3.ap-east-1.amazonaws.com/user-guide/GenBravo_UserGuide_web_';
  }
}

class UatConfig implements AppConfig {
  baseUrl = 'http://uat.app-generali.com';
  baseApiUrl = UAT_API_URL;
  baseCmsUrl: string;
  baseTeladocUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  paymentReturnUrl!: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: { bankTransferInfo: JsonUrl; chequeInfo: JsonUrl };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  captchaSiteKey: string;
  tagManagerArgs = {
    gtmId: GTM_UAT,
  };
  userGuideUrl: string;

  constructor() {
    this.baseCmsUrl = UAT_URL + '/site-content';
    this.baseTeladocUrl = BASE_TELADOC_URL;
    this.keycloak = {
      url: 'https://uat-api.generali.com.hk/auth-keycloak',
      realm: 'all-in-one-bravo',
      clientId: 'react-auth',
    };
    this.cmsUrl = getCmsUrl(this.baseCmsUrl);
    this.googleMapApiKey = 'AIzaSyCbwYjPPmBe-6bk0wbnuDG4L_gIXtaRsak';
    this.paymentReturnUrl = PAYMENT_RETURN_PATH;
    this.captchaSiteKey = CAPTCHA_SITE_KEY;
    this.userGuideUrl = 'https://aiob-sitecontent-uat.s3.ap-east-1.amazonaws.com/user-guide/GenBravo_UserGuide_web_';
  }
}

class UatConfig2 implements AppConfig {
  baseUrl = 'http://uat.app-generali.com';
  baseApiUrl = UAT_API_URL;
  baseCmsUrl: string;
  baseTeladocUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  paymentReturnUrl!: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: { bankTransferInfo: JsonUrl; chequeInfo: JsonUrl };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  captchaSiteKey: string;
  tagManagerArgs = {
    gtmId: GTM_UAT,
  };
  userGuideUrl: string;

  constructor() {
    this.baseCmsUrl = UAT2_URL + '/site-content';
    this.baseTeladocUrl = BASE_TELADOC_URL;
    this.keycloak = {
      url: 'https://uat-api.generali.com.hk/auth-keycloak',
      realm: 'all-in-one-bravo',
      clientId: 'react-auth',
    };
    this.cmsUrl = getCmsUrl(this.baseCmsUrl);
    this.googleMapApiKey = 'AIzaSyCbwYjPPmBe-6bk0wbnuDG4L_gIXtaRsak';
    this.paymentReturnUrl = PAYMENT_RETURN_PATH;
    this.captchaSiteKey = CAPTCHA_SITE_KEY;
    this.userGuideUrl = 'https://aiob-sitecontent-uat.s3.ap-east-1.amazonaws.com/user-guide/GenBravo_UserGuide_web_';
  }
}

class ProductionConfig implements AppConfig {
  baseUrl = 'http://eapp.generali.com.hk';
  baseApiUrl = PROD_API_URL;
  baseCmsUrl: string;
  baseTeladocUrl: string;
  keycloak: KeycloakConfig;
  googleMapApiKey: string;
  paymentReturnUrl!: string;
  cmsUrl: {
    json: {
      i18n: JsonUrl;
      shortfall: { bankTransferInfo: JsonUrl; chequeInfo: JsonUrl };
      faq: JsonUrl;
      lifeFaq: JsonUrl;
    };
  };
  tagManagerArgs = {
    gtmId: GTM_PROD,
  };
  captchaSiteKey: string;
  userGuideUrl: string;

  constructor() {
    this.baseCmsUrl = PROD_URL + '/site-content';
    // this.baseTeladocUrl = 'http://generali-hk.teladochealth.com.cn';
    //ITREQ-4904 - Update Teledoc Url
    this.baseTeladocUrl = 'https://generali-hk.teladochealthasia.com';
    this.keycloak = {
      url: 'https://api.generali.com.hk/auth-keycloak',
      realm: 'all-in-one-bravo',
      clientId: 'react-auth',
    };
    this.cmsUrl = getCmsUrl(this.baseCmsUrl);
    this.googleMapApiKey = 'AIzaSyCbwYjPPmBe-6bk0wbnuDG4L_gIXtaRsak';
    this.paymentReturnUrl = PAYMENT_RETURN_PATH;
    this.captchaSiteKey = CAPTCHA_SITE_KEY;
    this.userGuideUrl = 'https://aiob-sitecontent-prd.s3.ap-east-1.amazonaws.com/user-guide/GenBravo_UserGuide_web_';
  }
}

const config = new Map<string, AppConfig>();
config.set('mock', new DevelopmentConfig());
config.set('development', new DevelopmentConfig());
config.set('development2', new DevelopmentConfig2());
config.set('uat', new UatConfig());
config.set('uat2', new UatConfig2());
config.set('prod', new ProductionConfig());

const getConfig = (environment = process.env.REACT_APP_ENVIRONMENT || 'development'): AppConfig => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return config.get(environment ?? 'development') ?? config.get('development')!;
};

export default getConfig;

export const limittimes = 999;
